import { useId, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { addDays, todayKey } from '../../tasks/dates';
import { useCompleteTask } from '../../tasks/api';
import { useMeetingActions, useReview, type MeetingView } from '../api';
import { formatClock } from '../clock';
import styles from './Sidebar.module.css';

/**
 * The context of the conversation (Beratung 11/12, the sheets of 14): deal card, open tasks, the internal
 * note, and the markers of this conversation with „Stelle markieren" / „Aufgabe anlegen". No live transcript
 * exists (server decision): the „Transkript" tab lists the positions marked on the conversation clock.
 */

export type SidebarTab = 'deal' | 'transcript';

/**
 * „Stelle markieren" needs the running clock: the recording's in a meeting that records, the conversation's
 * otherwise (D-163).
 */
export function canMark(meeting: MeetingView): boolean {
  return meeting.recording.enabled
    ? meeting.recording.active
    : meeting.status === 'running' && meeting.startedAt !== null;
}

export function Sidebar({
  meeting,
  tab,
  onTab,
  clockSeconds,
}: {
  meeting: MeetingView;
  tab: SidebarTab;
  onTab: (tab: SidebarTab) => void;
  clockSeconds: () => number;
}) {
  const markers = useMarkers(meeting.id);
  const id = useId();
  return (
    <aside className={styles.sidebar} aria-label="Kontext">
      <div className={styles.tabs} role="tablist" aria-label="Kontext">
        {(['deal', 'transcript'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            id={`${id}-${value}-tab`}
            aria-selected={tab === value}
            aria-controls={`${id}-${value}`}
            className={clsx(styles.tab, tab === value && styles.tabActive)}
            onClick={() => onTab(value)}
          >
            {value === 'deal' ? 'Deal' : 'Transkript'}
          </button>
        ))}
        {tab === 'transcript' ? <span className={styles.tabsEnd}>{markedLabel(markers.count)}</span> : null}
      </div>
      <div
        id={`${id}-${tab}`}
        role="tabpanel"
        aria-labelledby={`${id}-${tab}-tab`}
        className={clsx(styles.panel, tab === 'transcript' && styles.panelTranscript)}
      >
        {tab === 'deal' ? (
          <>
            <DealCard meeting={meeting} />
            <OpenTasks meeting={meeting} />
            <NoteForm meeting={meeting} grow />
          </>
        ) : (
          <MarkersPanel meeting={meeting} clockSeconds={clockSeconds} />
        )}
      </div>
    </aside>
  );
}

export function markedLabel(count: number): string {
  return `${count} ${count === 1 ? 'Stelle' : 'Stellen'} markiert`;
}

/** The markers of this meeting (the review's marked lines, in time order). */
export function useMarkers(meetingId: string) {
  const review = useReview(meetingId);
  const lines = (review.data?.transcript.lines ?? []).filter((line) => line.marked);
  return { lines, count: review.data?.transcript.markedCount ?? lines.length, query: review };
}

export function DealCard({ meeting }: { meeting: MeetingView }) {
  const deal = meeting.context.deal;
  if (!deal) {
    return (
      <div className={styles.dealCard}>
        <div className={styles.eyebrow}>Kontakt</div>
        <div className={styles.dealTitle}>{meeting.appointment.contactName ?? 'Gast'}</div>
        <div className={styles.dealNone}>Kein Deal verknüpft</div>
      </div>
    );
  }
  const facts: Array<[string, string | null]> = [
    ['Phase', deal.stage],
    ['Kaufpreis', deal.purchasePrice],
    ['Fläche', deal.area],
    ['Betreuer', deal.advisor],
  ];
  return (
    <div className={styles.dealCard}>
      <div className={styles.eyebrow}>Deal #{deal.number}</div>
      {deal.title ? <div className={styles.dealTitle}>{deal.title}</div> : null}
      <dl className={styles.facts}>
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function OpenTasks({ meeting }: { meeting: MeetingView }) {
  const complete = useCompleteTask();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const tasks = meeting.context.openTasks;

  async function done(taskId: string) {
    setBusy(taskId);
    try {
      await complete.mutateAsync({ id: taskId, body: {} });
      await queryClient.invalidateQueries({ queryKey: queryRoots.meetings });
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section aria-labelledby={`${meeting.id}-tasks`}>
      <div id={`${meeting.id}-tasks`} className={styles.eyebrow}>
        Offene Aufgaben
      </div>
      {tasks.length === 0 ? (
        <div className={styles.empty}>Keine offenen Aufgaben.</div>
      ) : (
        <div className={styles.taskList}>
          {tasks.map((task) => (
            <label key={task.id} className={clsx(styles.task, task.done && styles.taskDone)}>
              <input
                type="checkbox"
                className={styles.srOnly}
                checked={task.done}
                disabled={task.done || busy !== null}
                onChange={() => void done(task.id)}
              />
              <span className={clsx(styles.taskBox, task.done && styles.taskBoxDone)} aria-hidden="true">
                {task.done ? <Check size={9} strokeWidth={3} /> : null}
              </span>
              <span>
                {task.title}
                {task.dueLabel && !task.done ? (
                  <span className={styles.taskDue}> · {task.dueLabel}</span>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      )}
    </section>
  );
}

export function NoteForm({ meeting, grow = false }: { meeting: MeetingView; grow?: boolean }) {
  const actions = useMeetingActions(meeting.id);
  const { toast } = useToast();
  const [text, setText] = useState('');
  const id = useId();

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return;
    try {
      await actions.note.mutateAsync(text.trim());
      setText('');
      toast({ message: 'Notiz in der Deal-Timeline gespeichert' });
    } catch (error) {
      toast({ message: errorMessage(error) });
    }
  }

  return (
    <form className={clsx(styles.note, grow && styles.noteGrow)} onSubmit={(event) => void save(event)}>
      <label htmlFor={`${id}-note`} className={styles.noteHead}>
        <span className={styles.eyebrow}>Notiz</span>
        <span className={styles.noteHint}>· intern, nur im CRM</span>
      </label>
      <textarea
        id={`${id}-note`}
        className={styles.textarea}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <button type="submit" className={styles.primary} disabled={!text.trim() || actions.note.isPending}>
        In Deal-Timeline speichern
      </button>
    </form>
  );
}

export function MarkersPanel({
  meeting,
  clockSeconds,
  startWith,
}: {
  meeting: MeetingView;
  /** Seconds since the conversation started (the recording clock when the meeting records). */
  clockSeconds: () => number;
  /** Mobile „Markieren" opens the sheet with the marker form. */
  startWith?: 'marker' | undefined;
}) {
  const markers = useMarkers(meeting.id);
  const [form, setForm] = useState<{ kind: 'marker'; seconds: number } | { kind: 'task' } | null>(() =>
    startWith === 'marker' ? { kind: 'marker', seconds: clockSeconds() } : null,
  );
  const recording = meeting.recording.enabled;
  const markable = canMark(meeting);

  return (
    <>
      <div className={styles.lines}>
        {markers.lines.length === 0 ? (
          <div className={styles.empty}>
            {recording
              ? 'Noch keine Stelle markiert. Das Transkript des Gesprächs erscheint im Rückblick.'
              : 'Noch keine Stelle markiert.'}
          </div>
        ) : (
          markers.lines.map((line) => (
            <div key={`${line.timeSeconds}-${line.text}`} className={styles.markedLine}>
              <span className={styles.lineTime}>{line.timeLabel}</span>
              <span className={styles.lineText}>
                {line.speakerName ? <span className={styles.speaker}>{line.speakerName} </span> : null}
                {line.text}
                {line.taskTitle ? (
                  <span className={styles.lineSub}>Markiert · wird Aufgabe „{line.taskTitle}“</span>
                ) : null}
              </span>
            </div>
          ))
        )}
      </div>
      {form?.kind === 'marker' ? (
        <MarkerForm meeting={meeting} seconds={form.seconds} onDone={() => setForm(null)} />
      ) : form?.kind === 'task' ? (
        <TaskForm meeting={meeting} onDone={() => setForm(null)} />
      ) : (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.glassButton}
            disabled={!markable}
            title={recording && !markable ? 'Die Aufzeichnung läuft noch nicht.' : undefined}
            onClick={() => setForm({ kind: 'marker', seconds: clockSeconds() })}
          >
            Stelle markieren
          </button>
          <button type="button" className={styles.glassButton} onClick={() => setForm({ kind: 'task' })}>
            Aufgabe anlegen
          </button>
        </div>
      )}
    </>
  );
}

function MarkerForm({
  meeting,
  seconds,
  onDone,
}: {
  meeting: MeetingView;
  seconds: number;
  onDone: () => void;
}) {
  const actions = useMeetingActions(meeting.id);
  const { toast } = useToast();
  const id = useId();
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [visible, setVisible] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) {
      setError('Bitte geben Sie einen Titel an.');
      return;
    }
    try {
      await actions.marker.mutateAsync({
        timeSeconds: seconds,
        taskTitle: title.trim(),
        ...(text.trim() ? { text: text.trim() } : {}),
        visibleToCustomer: visible,
      });
      toast({ message: `Stelle ${formatClock(seconds)} markiert` });
      onDone();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <form className={styles.form} onSubmit={(event) => void submit(event)} aria-label="Stelle markieren">
      <div className={styles.formHead}>Stelle {formatClock(seconds)}</div>
      <label className={styles.field} htmlFor={`${id}-title`}>
        <span className={styles.fieldLabel}>
          Aufgabe <span aria-hidden="true">*</span>
        </span>
        <input
          id={`${id}-title`}
          className={clsx(styles.input, error !== null && !title.trim() && styles.inputInvalid)}
          value={title}
          required
          autoFocus
          aria-invalid={error !== null && !title.trim()}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
      <label className={styles.field} htmlFor={`${id}-text`}>
        <span className={styles.fieldLabel}>Notiz</span>
        <input
          id={`${id}-text`}
          className={styles.input}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      </label>
      <label className={styles.check}>
        <input
          type="checkbox"
          className={styles.srOnly}
          checked={visible}
          onChange={(event) => setVisible(event.target.checked)}
        />
        <span className={clsx(styles.checkBox, visible && styles.checkBoxOn)} aria-hidden="true">
          {visible ? <Check size={10} strokeWidth={2.5} /> : null}
        </span>
        Sichtbar für den Mandanten
      </label>
      {error ? (
        <div className={styles.formError} role="alert">
          {error}
        </div>
      ) : null}
      <div className={styles.formActions}>
        <button type="button" className={styles.glassButton} onClick={onDone}>
          Abbrechen
        </button>
        <button type="submit" className={styles.primary} disabled={actions.marker.isPending}>
          Markieren
        </button>
      </div>
    </form>
  );
}

function TaskForm({ meeting, onDone }: { meeting: MeetingView; onDone: () => void }) {
  const actions = useMeetingActions(meeting.id);
  const { toast } = useToast();
  const id = useId();
  const [title, setTitle] = useState('');
  const [dueOn, setDueOn] = useState(() => addDays(todayKey(), 2));
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) {
      setError('Bitte geben Sie einen Titel an.');
      return;
    }
    try {
      await actions.task.mutateAsync({ title: title.trim(), dueOn, visibleToCustomer: visible });
      toast({ message: 'Aufgabe angelegt' });
      onDone();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <form className={styles.form} onSubmit={(event) => void submit(event)} aria-label="Aufgabe anlegen">
      <label className={styles.field} htmlFor={`${id}-title`}>
        <span className={styles.fieldLabel}>
          Aufgabe <span aria-hidden="true">*</span>
        </span>
        <input
          id={`${id}-title`}
          className={clsx(styles.input, error !== null && !title.trim() && styles.inputInvalid)}
          value={title}
          required
          autoFocus
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
      <label className={styles.field} htmlFor={`${id}-due`}>
        <span className={styles.fieldLabel}>Fällig am</span>
        <input
          id={`${id}-due`}
          type="date"
          className={styles.input}
          value={dueOn}
          required
          onChange={(event) => setDueOn(event.target.value)}
        />
      </label>
      <label className={styles.check}>
        <input
          type="checkbox"
          className={styles.srOnly}
          checked={visible}
          onChange={(event) => setVisible(event.target.checked)}
        />
        <span className={clsx(styles.checkBox, visible && styles.checkBoxOn)} aria-hidden="true">
          {visible ? <Check size={10} strokeWidth={2.5} /> : null}
        </span>
        Sichtbar für den Mandanten
      </label>
      {error ? (
        <div className={styles.formError} role="alert">
          {error}
        </div>
      ) : null}
      <div className={styles.formActions}>
        <button type="button" className={styles.glassButton} onClick={onDone}>
          Abbrechen
        </button>
        <button type="submit" className={styles.primary} disabled={actions.task.isPending || !dueOn}>
          Anlegen
        </button>
      </div>
    </form>
  );
}
